<template>
  <div>
    <serveur-info :erreur="error" />
    <my-notification :icon="'eur'">
      <template #title>
        {{ $t('compCarbonFormsPurchasesMain.title-1') }}
      </template>
      <div class="content">
        {{ $t('compCarbonFormsPurchasesMain.info-1') }}
        <strong> {{ currentYear }}</strong
        >{{ $t('compCarbonFormsPurchasesMain.info-2') }}
      </div>
    </my-notification>
    <br />
    <help-file-format :module="module" :hasExtraction="true" />
    <b-notification :has-icon="true" icon="info" :closable="false">
      <span v-html="$t('compCarbonFormsPurchasesMain.info-7')" />
      <br />
      <span v-html="$t('compCarbonFormsPurchasesMain.info-8')" />
    </b-notification>

    <b-loading
      :is-full-page="true"
      v-model="isLoading"
      :can-cancel="false"
    ></b-loading>

    <purchases-warning ref="purchasesWarning" />

    <upload
      v-model="source"
      :disabled="isSubmitted"
      :module="module"
      :infos="infos"
      :callback="callback"
      :formatDescription="formatDescription"
      @update="updateUpload"
    />

    <purchases-table
      @edit="edit"
      @update="updateFTable"
      @del="del"
      :items="purchases"
      data-testid="purchases-table"
    />

    <b-modal
      :active="displayModal"
      has-modal-card
      @close="closeModal"
      :can-cancel="['escape', 'x', 'outside']"
      :width="1000"
    >
      <div class="modal-card" style="width: 1000px">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ modalTitle }}</p>
        </header>
        <section class="modal-card-body">
          <purchase-form
            :purchase="purchase"
            :modeCreation="modeCreation"
            @close="closeModal"
            @add="add"
            @update="updateItem"
          />
        </section>
      </div>
    </b-modal>

    <br />
    <nav class="level">
      <div class="level-left"></div>
      <div class="level-right">
        <div class="buttons">
          <b-button
            class="button"
            icon-left="plus"
            :disabled="isSubmitted"
            @click="displayAddForm()"
          >
            {{ $t('add') }}
          </b-button>
          <b-button
            class="level-item"
            :icon-left="validSavedIcon"
            :disabled="!flags.isModified"
            type="is-primary"
            @click="validate()"
            data-testid="purchases-save"
          >
            <span v-if="authStore.isAuthenticated">{{ $t('save') }}</span>
            <span v-else>{{ $t('validate') }}</span>
          </b-button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import Purchase from '@/models/carbon/Purchase.js'
import Modules from '@/models/Modules.js'
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useAdminStore } from '@/stores/admin.js'
import { useGHGIStore } from '@/stores/ghgi.js'

export default {
  name: 'Main',
  components: {
    'my-notification': () => import('@/components/MyNotification.vue'),
    'serveur-info': () => import('@/components/ServeurInfo.vue'),
    'purchase-form': () =>
      import('@/components/carbon/forms/purchases/Form.vue'),
    'help-file-format': () =>
      import('@/components/carbon/forms/HelpFileFormat.vue'),
    upload: () => import('@/components/carbon/forms/Upload.vue'),
    'purchases-warning': () =>
      import('@/components/carbon/forms/purchases/Warning.vue'),
    'purchases-table': () =>
      import('@/components/carbon/forms/purchases/Table.vue')
  },
  $_veeValidate: {
    validator: 'new'
  },
  props: {
    flags: {
      type: Object
    }
  },
  data () {
    return {
      error: null,
      source: null,
      modeCreation: false,
      displayModal: false,
      displayNote: false,
      purchase: null,
      isLoading: false,
      showPurchases: 'all'
    }
  },
  setup () {
    const authStore = useAuthenticationStore()
    const adminStore = useAdminStore()
    const ghgiStore = useGHGIStore()
    return { authStore, adminStore, ghgiStore }
  },
  computed: {
    module: function () {
      return Modules.PURCHASES
    },
    validSavedIcon: function () {
      let icon = null
      if (this.authStore.isAuthenticated) {
        icon = 'save'
      } else {
        icon = 'check'
      }
      return icon
    },
    formatDescription: function () {
      return Purchase.formatDescription
    },
    infos: function () {
      let totalValid = this.ghgiStore.validItems(
        this.module,
        this.source
      ).length
      let totalInvalid = this.ghgiStore.invalidItems(
        this.module,
        this.source
      ).length
      let totalOtherModules = this.ghgiStore.otherItems(
        this.module,
        this.source
      ).length
      let totalAmount = this.purchases
        .filter((p) => p.isValid())
        .map((obj) => obj.amount)
        .reduce((a, b) => parseFloat(a) + parseFloat(b), 0.0)
      let value1 = this.$options.filters.splitNumber(totalValid) + ' / '
      value1 += this.$options.filters.splitNumber(totalValid + totalInvalid)
      return [
        {
          label: this.$t('compCarbonFormsPurchasesMain.label-1'),
          value: value1
        },
        {
          label: this.$t('compCarbonFormsPurchasesMain.label-2'),
          value: this.$options.filters.splitNumber(totalAmount) + ' €'
        },
        {
          label: this.$t('compCarbonFormsPurchasesMain.label-3'),
          value: this.$options.filters.splitNumber(totalOtherModules)
        }
      ]
    },
    currentYear () {
      return this.ghgiStore.item.year
    },
    purchases: function () {
      return this.ghgiStore.items(this.module).filter((item) => {
        if (this.source === null) {
          return true
        } else {
          return item.source === this.source
        }
      })
    },
    isSubmitted: function () {
      return this.ghgiStore.item.isSubmitted
    },
    modalTitle: function () {
      let title = this.$t('compCarbonFormsPurchasesMain.modal-title-1')
      if (this.modeCreation) {
        title = this.$t('compCarbonFormsPurchasesMain.modal-title-2')
      } else if (this.isSubmitted) {
        title = this.$t('compCarbonFormsPurchasesMain.modal-title-3')
      }
      return title
    }
  },
  methods: {
    callback: function (data, format, source) {
      this.error = null
      this.isLoading = true
      let allTags = this.adminStore.allTagCategories
        .map((obj) => obj.tags)
        .flat()
      let purchases = Purchase.createFromLines(data, format, allTags, source)
      for (let purchase of purchases) {
        this.ghgiStore.addItem({
          module: this.module,
          item: purchase
        })
      }
      this.isLoading = false
    },
    updateUpload: function () {
      this.$emit('is-modified', true)
      this.forceComputation()
    },
    validate: function () {
      let self = this
      if (this.ghgiStore.invalidItems(this.module).length !== 0) {
        this.$buefy.dialog.confirm({
          title: self.$t('compCarbonFormsPurchasesMain.title-2'),
          message: self.$t('compCarbonFormsPurchasesMain.info-9'),
          confirmText: self.$t('validate'),
          cancelText: self.$t('cancel'),
          canCancel: ['escape', 'button', 'outside'],
          onConfirm: function () {
            self.ghgiStore.deleteInvalidItems(self.module)
            self.subValidate()
          }
        })
      } else {
        this.subValidate()
      }
    },
    subValidate: function () {
      if (this.authStore.isAuthenticated) {
        const loadingComponent = this.$buefy.loading.open({
          container: null
        })
        this.ghgiStore
          .saveItems(this.module)
          .then((data) => {
            loadingComponent.close()
            this.$emit('is-valid', this.ghgiStore.items(this.module).length > 0)
            this.$emit('is-modified', false)
            this.error = ''
            window.scrollTo(0, 0)
            this.$buefy.toast.open({
              type: 'is-light',
              message: this.$t('compCarbonFormsPurchasesMain.save-msg')
            })
            this.source = null
          })
          .catch((error) => {
            loadingComponent.close()
            this.$emit('is-valid', false)
            this.error = error.response.data
          })
      } else {
        this.$emit('is-valid', this.ghgiStore.items(this.module).length > 0)
        this.$emit('is-modified', false)
      }
    },
    closeModal: function () {
      this.displayModal = false
      this.purchase = new Purchase()
    },
    add: function (purchase) {
      this.ghgiStore.addItem({
        module: this.module,
        item: purchase
      })
      this.$emit('is-modified', true)
      this.closeModal()
      this.forceComputation()
    },
    updateItem: function (obj) {
      this.ghgiStore.updateItem({
        module: this.module,
        item: obj
      })
      this.$emit('is-modified', true)
      this.closeModal()
      this.forceComputation()
    },
    updateFTable: function () {
      this.$emit('is-modified', true)
      this.forceComputation()
    },
    del: function (items) {
      this.ghgiStore.deleteItems({
        module: this.module,
        items: items
      })
    },
    edit: function (purchase) {
      this.modeCreation = false
      this.purchase = purchase
      this.displayModal = true
    },
    displayAddForm: function () {
      this.modeCreation = true
      this.purchase = new Purchase()
      this.displayModal = true
    },
    forceComputation: function () {
      this.$refs.purchasesWarning.forceComputation()
    }
  }
}
</script>
